import StyleSheet from '../utils/helpers/styleSheet'
import RenderImg from './editArea/Img'
import {useAppDispatch} from '../stores/configureStore'
import {flipSide, setPrintArea, setZoom} from '../stores/editor/editor.reducer'
import {useRouteMatch} from 'react-router-dom'
import {useEffect, useRef, useState} from 'react'
import services from '../utils/services'
import * as htmlToImage from 'html-to-image'
import download from 'downloadjs'
import {origo} from '../utils/helpers/origo'
import {Select} from './global/Select'
import {range} from '../utils/helpers/range'
import {Page} from '../models/editor/page'
import {GetProductResponse} from '../models/dto/getProduct.response'

const styles: StyleSheet = {
  page: {
    boxSizing: 'border-box',
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    userSelect: 'none',
    overflow: 'hidden',
  },
}

interface MatchParams {
  productId: string
  id: string
}

interface Correction {
  printWidth: number
  printHeight: number
  scale: number
  x: number
  y: number
}

declare global {
  interface Window {
    getPages: () => Array<number>
    selectPage: (index: number) => void
  }
}

export default function PrintView() {
  const route = useRouteMatch<MatchParams>()
  const dispatch = useAppDispatch()
  const [correction, setCorrection] = useState<Correction | null>(null)
  const [loading, setLoading] = useState(false)
  const printedRef = useRef<HTMLDivElement | null>(null)
  const [selectedPageIndex, setSelectedPageIndex] = useState<
    number | undefined
  >(undefined)
  const [pages, setPages] = useState<Page[]>([])
  const [product, setProduct] = useState<GetProductResponse | null>(null)

  useEffect(() => {
    window.getPages = () => {
      return pages.map((_, index) => index)
    }
    window.selectPage = (index: number) => {
      setSelectedPageIndex(() => index)
    }
  }, [pages])

  const print = async () => {
    if (!printedRef.current) {
      return
    }
    setLoading(() => true)
    const png = await htmlToImage.toPng(printedRef.current, {pixelRatio: 5})
    setLoading(() => false)
    download(png, 'image.png', 'image/png')
  }

  useEffect(() => {
    const loadStoredState = async () => {
      if (route.params.id && route.params.productId) {
        const response = await services.getStoredState(route.params.id)
        dispatch(setZoom({level: 0.2, translate: origo}))
        dispatch(
          flipSide({
            images: response.data.pages[0].images,
            maskImage: response.data.pages[0].mask,
          }),
        )
        if (response.data.pages[0].printArea) {
          dispatch(setPrintArea({printArea: response.data.pages[0].printArea}))
        }
        const productResponse = await services.getProduct(
          Number(route.params.productId),
        )
        setProduct(productResponse.data)
        setPages(response.data.pages)
        setSelectedPageIndex(0)
      }
    }

    loadStoredState()
  }, [route.params.id, route.params.productId, dispatch])

  useEffect(() => {
    if (!product || selectedPageIndex === undefined) {
      return
    }
    if (product.twoSided && selectedPageIndex === 1) {
      setCorrection(() => {
        switch (product.pricing.editableAreaBack) {
          case 'a3':
            return {
              scale: product.sides.back.scale,
              x: product.sides.back.position.x,
              y: product.sides.back.position.y,
              printWidth: product.pricing.a3WidthMm,
              printHeight: product.pricing.a3HeightMm,
            }
          case 'a4':
            return {
              scale: product.sides.back.scale,
              x: product.sides.back.position.x,
              y: product.sides.back.position.y,
              printWidth: product.pricing.a4WidthMm,
              printHeight: product.pricing.a4HeightMm,
            }
          case 'szivoldal':
            return {
              scale: product.sides.back.scale,
              x: product.sides.back.position.x,
              y: product.sides.back.position.y,
              printWidth: product.pricing.szivoldalWidthMm,
              printHeight: product.pricing.szivoldalHeightMm,
            }
          default:
            return null
        }
      })
    } else {
      setCorrection(() => {
        switch (product.pricing.editableArea) {
          case 'a3':
            return {
              scale: product.sides.front.scale,
              x: product.sides.front.position.x,
              y: product.sides.front.position.y,
              printWidth: product.pricing.a3WidthMm,
              printHeight: product.pricing.a3HeightMm,
            }
          case 'a4':
            return {
              scale: product.sides.front.scale,
              x: product.sides.front.position.x,
              y: product.sides.front.position.y,
              printWidth: product.pricing.a4WidthMm,
              printHeight: product.pricing.a4HeightMm,
            }
          case 'szivoldal':
            return {
              scale: product.sides.front.scale,
              x: product.sides.front.position.x,
              y: product.sides.front.position.y,
              printWidth: product.pricing.szivoldalWidthMm,
              printHeight: product.pricing.szivoldalHeightMm,
            }
          default:
            return null
        }
      })
    }
  }, [product, selectedPageIndex])

  return correction && selectedPageIndex !== undefined ? (
    <>
      <div>
        <button onClick={print} disabled={loading}>
          Nyomtatás
        </button>
        <Select
          options={range(pages.length).map(item => ({
            text: `${item + 1}. oldal`,
            value: item.toString(),
          }))}
          value={selectedPageIndex.toString()}
          onChange={event => {
            setSelectedPageIndex(Number(event.target.value))
          }}
        />
      </div>
      <div
        ref={printedRef}
        style={{
          ...styles.page,
          width: `${correction.printWidth}mm`,
          height: `${correction.printHeight}mm`,
        }}>
        <div
          style={{
            height: '100%',
            width: '100%',
            transform: `scale(${
              1 / correction.scale
            }) translate(${-correction.x}px, ${-correction.y}px)`,
          }}>
          {pages[selectedPageIndex].images.map(image => (
            <RenderImg img={image} key={`editorImageList_${image.id}`} noZoom />
          ))}
        </div>
      </div>
    </>
  ) : null
}
